// Etterretnings-arbeidsflate (INTEL): data → informasjon → etterretning.
// Analytikeren fører produkter, vurderer kilde/troverdighet (STANAG 2511),
// graderer, kobler til stridsmeldinger, og deler OPP (stab) + SIDEVEIS (områder).
// Gjenbrukes docked (hovedvindu) og standalone (pop-out). Ren DOM; all
// felt-avledet tekst settes via textContent (ingen XSS). Modellen er forberedt
// for LLM: «Generer utkast» er deterministisk nå og kan byttes med KI senere.
import {
  Classification, Reliability, Credibility, Discipline, RELIABILITY_LABEL, CREDIBILITY_LABEL,
  DISCIPLINE_LABEL, SUBDISCIPLINES, CLASSIFICATION_COLOR, admiraltyCode, reportSummary,
  formatMgrs, latLonToMgrs, gyldigTidsrom,
  type IntelProduct, type CombatReport,
} from '@skade/contracts';
import type { User } from '@skade/rbac';
import { ImintAnnotator } from './imint.js';
import { tolkMgrsFelt, msTilDatetimeLocal, datetimeLocalTilMs } from './stedtid.js';

export interface IntelOpts {
  user: User;
  onShare: (p: IntelProduct) => void;
  getReports?: () => CombatReport[];
  onFocus?: (lat: number, lon: number) => void;
  /**
   * Kartsenteret NÅ (UX-funn 8): gir «Bruk kartsenter»-knappen i sted-feltet,
   * så analytikeren kan peke ut stedet i kartet i stedet for å taste MGRS.
   * Valgfri — et poppet-ut intel-vindu har ikke noe kart, og da vises bare
   * det manuelle feltet (som valideres med MGRS-hjelperne fra kontraktene).
   */
  hentKartsenter?: () => { lat: number; lon: number } | null;
  /**
   * KART FØRST (UX_DESIGNSYSTEM.md §2, Bølge 241/SENS-45). Arm neste
   * kartklikk; kalles med MGRS-referansen for punktet. «Bruk kartsenter»
   * over krever at analytikeren FØRST panorerer kartet dit — dette lar hen
   * KLIKKE stedet direkte, samme primærvei som RFI-panelet og
   * kapabilitetsplanleggeren fikk. Valgfri av samme grunn som `hentKartsenter`.
   */
  armStedKlikk?: (ferdig: (mgrs: string) => void) => void;
}

let idSeq = 0;
const CLASSES = Classification.options;
const RELS = Reliability.options;
const CREDS = Credibility.options;
const DISCIPLINES = Discipline.options;

export class IntelWorkspace {
  readonly el: HTMLElement;
  private products = new Map<string, IntelProduct>();
  private listEl!: HTMLElement;
  private editorEl!: HTMLElement;
  private filter = 'ALLE';

  constructor(private opts: IntelOpts) {
    this.el = div('intel-ws');
    this.build();
    this.renderList();
  }

  /** Alle kjente produkter (til etterretningspakke-eksport). */
  list(): IntelProduct[] {
    return [...this.products.values()];
  }

  /** Ta imot et produkt utenfra (collab) — nyest vinner. */
  ingest(p: IntelProduct) {
    const cur = this.products.get(p.id);
    if (!cur || p.updatedMs >= cur.updatedMs) {
      this.products.set(p.id, p);
      this.renderList();
    }
  }

  private build() {
    const head = div('intel-head');
    head.append(txt('intel-title', 'Etterretning'));
    const nb = document.createElement('button');
    nb.className = 'intel-new'; nb.textContent = '＋ Nytt produkt';
    nb.onclick = () => this.openEditor(null);
    head.append(nb);
    this.el.append(head);

    // Filter på disiplin (IMINT/OSINT/…).
    const filterRow = div('intel-filter');
    filterRow.append(txt('intel-field-label', 'Disiplin'));
    const fsel = document.createElement('select');
    fsel.className = 'intel-input';
    const all = document.createElement('option'); all.value = 'ALLE'; all.textContent = 'Alle disipliner'; fsel.append(all);
    for (const d of DISCIPLINES) { const o = document.createElement('option'); o.value = d; o.textContent = DISCIPLINE_LABEL[d]; fsel.append(o); }
    fsel.onchange = () => { this.filter = fsel.value; this.renderList(); };
    filterRow.append(fsel);
    this.el.append(filterRow);

    this.listEl = div('intel-list');
    this.el.append(this.listEl);
    this.editorEl = div('intel-editor');
    this.el.append(this.editorEl);
  }

  private renderList() {
    const arr = [...this.products.values()]
      .filter((p) => this.filter === 'ALLE' || p.discipline === this.filter)
      .sort((a, b) => b.updatedMs - a.updatedMs);
    this.listEl.innerHTML = '';
    if (!arr.length) { this.listEl.append(txt('intel-empty', 'Ingen etterretningsprodukter')); return; }
    for (const p of arr) {
      const row = document.createElement('button');
      row.className = 'intel-row';
      const badge = txt('intel-badge', p.classification);
      badge.style.background = CLASSIFICATION_COLOR[p.classification];
      const disc = txt('intel-disc', p.discipline);
      const code = txt('intel-code', admiraltyCode(p));
      const title = txt('intel-row-title', p.title);
      row.append(badge, disc, code, title);
      row.onclick = () => this.openView(p);
      this.listEl.append(row);
    }
  }

  private openView(p: IntelProduct) {
    const own = p.author === this.opts.user.id;
    this.editorEl.innerHTML = '';
    this.editorEl.classList.add('open');
    const card = div('intel-card');
    const badge = txt('intel-badge', p.classification);
    badge.style.background = CLASSIFICATION_COLOR[p.classification];
    const head = div('intel-card-head');
    head.append(badge, txt('intel-code', admiraltyCode(p)));
    if (this.opts.onFocus && p.lat !== undefined && p.lon !== undefined) {
      const g = iconBtn('⌖', () => this.opts.onFocus?.(p.lat!, p.lon!)); g.title = 'Vis på kartet';
      head.append(g);
    }
    head.append(iconBtn('×', () => this.closeEditor()));
    card.append(head, txt('intel-card-title', p.title));
    card.append(kv('Disiplin', DISCIPLINE_LABEL[p.discipline] + (p.subDiscipline ? ` · ${p.subDiscipline}` : '')));
    card.append(kv('Kilde', RELIABILITY_LABEL[p.reliability]));
    card.append(kv('Info', CREDIBILITY_LABEL[p.credibility]));
    // ── Rom/tid (UX-funn 8). Eldre produkter uten sted vises fortsatt — med et
    // ærlig «ikke angitt», ikke en tom rad som ser ut som en feil.
    card.append(kv('Sted', stedTekst(p)));
    card.append(kv('Gjelder', tidsromTekst(p)));
    const body = div('intel-body'); body.textContent = p.body || '(ingen tekst)';
    card.append(body);
    if (p.imageUrl) {
      const host = div('imint-host');
      host.append(new ImintAnnotator(p.imageUrl, p.boxes ?? [], true).el); // skrivebeskyttet
      card.append(host);
    }
    const aud: string[] = [];
    if (p.audience.up) aud.push('oppover (stab)');
    if (p.audience.areas.length) aud.push(`sideveis: ${p.audience.areas.join(', ')}`);
    card.append(kv('Delt', aud.length ? aud.join(' · ') : 'kun egen enhet'));
    card.append(kv('Forfatter', shortId(p.author)));
    if (own) {
      const edit = document.createElement('button');
      edit.className = 'intel-save'; edit.textContent = 'Rediger';
      edit.onclick = () => this.openEditor(p);
      card.append(edit);
    }
    this.editorEl.append(card);
  }

  /**
   * Start et nytt produkt FRA en observasjon (feltbilde, kamerasnapshot).
   *
   * ── HVORFOR DENNE FINNES ────────────────────────────────────────────────
   * IMINT-annotatoren og produkt-editoren har vært på plass hele tiden, men
   * veien inn gikk gjennom et tekstfelt der analytikeren måtte LIME INN en
   * bilde-URL manuelt. Da en soldat sendte et bilde fra GNÅ, måtte altså noen
   * finne URL-en i sporpanelet, kopiere den, åpne Etterretning, lage nytt
   * produkt og lime den inn — seks steg som i praksis betyr at det ikke skjer.
   *
   * Dette er broen: observasjon → ferdig utfylt produktutkast med bildet, stedet
   * og kildesporingen på plass, klar til annotering. Ingen ny motor; bare
   * ledningen som manglet (eierfunn 2026-08-17).
   *
   * ⚠ UTKASTET LAGRES IKKE AV SEG SELV. Editoren åpnes, og analytikeren må
   * lagre. Å skrive produktet direkte ville laget etterretningsprodukter ingen
   * har vurdert — og et produkt med automatisk «pålitelighet» er verre enn ingen.
   */
  nyttProduktFraObservasjon(o: {
    imageUrl: string;
    tittel: string;
    /** Fritekst-grunnlag: hva sensoren var, retning, tid — det sporet alt vet. */
    grunnlag?: string;
    lat?: number;
    lon?: number;
    mgrs?: string;
    /** Observasjonstidspunktet — produktets gyldighet starter der, ikke «nå». */
    observertMs?: number;
  }) {
    const naa = Date.now();
    const utkast: IntelProduct = {
      id: `imint-${naa}-${Math.round(naa % 100000)}`,
      title: o.tittel.slice(0, 500),
      body: o.grunnlag ?? '',
      // IMINT er riktig disiplin for et bilde. Analytikeren kan endre den.
      discipline: 'IMINT',
      classification: 'UGRADERT',
      // ⚠ PÅLITELIGHET/TROVERDIGHET SETTES IKKE AUTOMATISK.
      // NATO-skalaen (A–F / 1–6) er en ANALYTIKERVURDERING av kilde og innhold.
      // «F» og «6» betyr «kan ikke bedømmes» — det ærlige utgangspunktet for et
      // utkast ingen har vurdert ennå. Å sette f.eks. B2 fordi bildet kom fra en
      // egen soldat ville gjort maskinen til analytiker.
      reliability: 'F',
      credibility: '6',
      author: this.opts.user.id,
      org: { district: this.opts.user.org?.district ?? '' },
      tsMs: naa,
      updatedMs: 0,
      audience: { up: false, areas: [] },
      linkedReportIds: [],
      ...(o.lat !== undefined ? { lat: o.lat } : {}),
      ...(o.lon !== undefined ? { lon: o.lon } : {}),
      ...(o.mgrs ? { mgrs: o.mgrs } : {}),
      ...(o.observertMs ? { gjelderFraMs: o.observertMs } : {}),
      imageUrl: o.imageUrl,
      boxes: [],
    };
    this.openEditor(utkast);
  }

  private openEditor(existing: IntelProduct | null) {
    this.editorEl.innerHTML = '';
    this.editorEl.classList.add('open');
    const card = div('intel-card');
    card.append(txt('intel-card-title', existing ? 'Rediger produkt' : 'Nytt etterretningsprodukt'));

    const title = input('Tittel', existing?.title ?? '');
    const disc = select('Disiplin', DISCIPLINES, existing?.discipline ?? 'ALLKILDE', (v) => DISCIPLINE_LABEL[v as Discipline]);
    // Underdisiplin: fritekst med forslag (datalist) basert på valgt disiplin.
    const subWrap = div('intel-field');
    subWrap.append(txt('intel-field-label', 'Underdisiplin'));
    const subInput = document.createElement('input');
    subInput.className = 'intel-input';
    subInput.value = existing?.subDiscipline ?? '';
    const dl = document.createElement('datalist');
    dl.id = `subdisc-${idSeq++}`;
    subInput.setAttribute('list', dl.id);
    const fillDl = () => {
      dl.innerHTML = '';
      for (const s of SUBDISCIPLINES[disc.field.value as Discipline] ?? []) {
        const o = document.createElement('option'); o.value = s; dl.append(o);
      }
    };
    fillDl();
    disc.field.onchange = fillDl;
    subWrap.append(subInput, dl);
    const cls = select('Gradering', CLASSES, existing?.classification ?? 'UGRADERT');
    const rel = select('Kildepålitelighet', RELS, existing?.reliability ?? 'F', (v) => RELIABILITY_LABEL[v as Reliability]);
    const cred = select('Troverdighet', CREDS, existing?.credibility ?? '6', (v) => CREDIBILITY_LABEL[v as Credibility]);
    card.append(title.wrap, disc.wrap, subWrap, cls.wrap, rel.wrap, cred.wrap);

    // ── STED + TID (UX-funn 8: rom/tid manglet HELT i produktet) ────────────
    // Nye produkter KREVER sted — en vurdering uten sted kan ikke plottes eller
    // avgrenses. Eksisterende produkter uten sted kan fortsatt åpnes og vises
    // (bakoverkompatibelt); kravet gjelder ved opprettelse.
    // Forhåndsutfylling: lagret mgrs, ellers utledet av lagret lat/lon (eldre
    // produkter hadde bare grader).
    const stedStart = existing?.mgrs
      ?? (existing?.lat !== undefined && existing?.lon !== undefined
        ? latLonToMgrs({ lat: existing.lat, lon: existing.lon })
        : '');
    const sted = input('Sted (MGRS)' + (existing ? '' : ' — påkrevd'), formatMgrs(stedStart));
    sted.field.placeholder = 'f.eks. 32V NM 02345 66890';
    // KART FØRST: klikk-i-kartet er PRIMÆRVEIEN — «Bruk kartsenter» under er
    // fallback for når analytikeren allerede har panorert dit, ikke omvendt.
    if (this.opts.armStedKlikk) {
      const kartBtn = document.createElement('button');
      kartBtn.type = 'button';
      kartBtn.className = 'intel-gen intel-sted-velg';
      kartBtn.textContent = '⌖ Plasser i kartet';
      kartBtn.onclick = () => {
        this.opts.armStedKlikk!((mgrs) => { sted.field.value = formatMgrs(mgrs); });
      };
      sted.wrap.append(kartBtn);
    }
    // «Bruk kartsenter» bare når et kart finnes å lese av (hovedvinduet) —
    // knappen skriver en LESBAR verdi inn i feltet, den lagrer ingenting selv.
    if (this.opts.hentKartsenter) {
      const velg = document.createElement('button');
      velg.type = 'button';
      velg.className = 'intel-gen intel-sted-velg';
      velg.textContent = '⌖ Bruk kartsenter (trådkorset)';
      velg.onclick = () => {
        const c = this.opts.hentKartsenter?.();
        const mgrs = c ? latLonToMgrs({ lat: c.lat, lon: c.lon }) : '';
        if (mgrs) sted.field.value = formatMgrs(mgrs);
      };
      sted.wrap.append(velg);
    }
    const fra = datofelt('Gjelder fra', existing?.gjelderFraMs);
    const til = datofelt('Gjelder til', existing?.gjelderTilMs);
    card.append(sted.wrap, fra.wrap, til.wrap);

    // Koble til stridsmeldinger (case) + generer utkast.
    const linked = new Set(existing?.linkedReportIds ?? []);
    const reports = this.opts.getReports?.() ?? [];
    const body = textarea('Vurdering / tekst', existing?.body ?? '');
    if (reports.length) {
      const lbl = txt('intel-field-label', 'Koble til meldinger');
      const box = div('intel-link-box');
      for (const r of reports.slice(0, 30)) {
        const id = `lk-${r.id}`;
        const row = document.createElement('label'); row.className = 'intel-link-row';
        const cb = document.createElement('input'); cb.type = 'checkbox'; cb.checked = linked.has(r.id);
        cb.onchange = () => (cb.checked ? linked.add(r.id) : linked.delete(r.id));
        const t = document.createElement('span'); t.textContent = `${r.type}: ${reportSummary(r)}`;
        row.append(cb, t); row.htmlFor = id; box.append(row);
      }
      const gen = document.createElement('button');
      gen.className = 'intel-gen'; gen.textContent = 'Generer utkast fra valgte meldinger';
      gen.onclick = () => {
        const chosen = reports.filter((r) => linked.has(r.id));
        if (!chosen.length) return;
        body.field.value = draftFromReports(chosen);
      };
      card.append(lbl, box, gen);
    }
    card.append(body.wrap);

    // IMINT: bilde-URL + bounding-box-annotering.
    const imgUrl = input('IMINT bilde-URL (valgfritt)', existing?.imageUrl ?? '');
    const annotHost = div('imint-host');
    let annotator: ImintAnnotator | null = null;
    const loadImg = document.createElement('button');
    loadImg.className = 'intel-gen'; loadImg.textContent = 'Last bilde for annotering (tegn bokser)';
    loadImg.onclick = () => {
      const u = imgUrl.field.value.trim();
      if (!u) return;
      annotHost.innerHTML = '';
      annotator = new ImintAnnotator(u, annotator ? annotator.getBoxes() : existing?.boxes ?? []);
      annotHost.append(annotator.el);
    };
    if (existing?.imageUrl) {
      annotator = new ImintAnnotator(existing.imageUrl, existing.boxes ?? []);
      annotHost.append(annotator.el);
    }
    card.append(imgUrl.wrap, loadImg, annotHost);

    // Målgruppe.
    const audWrap = div('intel-field');
    audWrap.append(txt('intel-field-label', 'Deling'));
    const upRow = document.createElement('label'); upRow.className = 'intel-link-row';
    const upCb = document.createElement('input'); upCb.type = 'checkbox'; upCb.checked = existing?.audience.up ?? false;
    upRow.append(upCb, document.createTextNode(' Del oppover (distrikt/HV-stab)'));
    const areas = input('Sideveis områder (kommaseparert, f.eks. HV-05, HV-01)', (existing?.audience.areas ?? []).join(', '));
    audWrap.append(upRow, areas.wrap);
    card.append(audWrap);

    // Feilboks som BLIR STÅENDE mens analytikeren retter — samme grunn som i
    // stridsmelding-komponisten: en alarm som forsvinner kan ikke handles på.
    const feilboks = txt('intel-feil', '');
    feilboks.hidden = true;
    const visFeil = (t: string, felt?: HTMLElement) => {
      feilboks.hidden = false;
      feilboks.textContent = t;
      felt?.focus();
    };
    card.append(feilboks);

    const save = document.createElement('button');
    save.className = 'intel-save'; save.textContent = existing ? 'Lagre og del' : 'Opprett og del';
    save.onclick = () => {
      if (!title.field.value.trim()) { visFeil('Tittel mangler.', title.field); return; }
      // Sted: valider ALLTID når noe er skrevet; KREV for nye produkter.
      const stedSvar = tolkMgrsFelt(sted.field.value);
      if (stedSvar === null) { visFeil('Sted er ikke en gyldig MGRS-referanse — f.eks. 32V NM 02345 66890.', sted.field); return; }
      if (stedSvar === 'tom' && !existing) { visFeil('Sted (MGRS) er påkrevd for nye produkter — skriv referansen eller bruk kartsenteret.', sted.field); return; }
      const gjelderFraMs = datetimeLocalTilMs(fra.field.value);
      const gjelderTilMs = datetimeLocalTilMs(til.field.value);
      if (!gyldigTidsrom(gjelderFraMs, gjelderTilMs)) { visFeil('«Gjelder til» er før «Gjelder fra» — vinduet kan ikke slutte før det begynner.', til.field); return; }
      feilboks.hidden = true;
      const now = Date.now();
      const p: IntelProduct = {
        id: existing?.id ?? `intel-${now.toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`,
        title: title.field.value.trim(),
        body: body.field.value.trim(),
        discipline: disc.field.value as Discipline,
        subDiscipline: subInput.value.trim() || undefined,
        classification: cls.field.value as Classification,
        reliability: rel.field.value as Reliability,
        credibility: cred.field.value as Credibility,
        author: existing?.author ?? this.opts.user.id,
        org: { district: this.opts.user.org.district, area: this.opts.user.org.area },
        tsMs: existing?.tsMs ?? now,
        updatedMs: now,
        audience: {
          up: upCb.checked,
          areas: areas.field.value.split(',').map((s) => s.trim()).filter(Boolean),
        },
        linkedReportIds: [...linked],
        // Sted: nytt feltinnhold vinner; tømt felt på et eldre produkt beholder
        // gamle grader (å slette et sted skal være et bevisst valg, ikke en
        // bieffekt av at feltet sto tomt ved en annen retting).
        lat: stedSvar === 'tom' ? existing?.lat : stedSvar.lat,
        lon: stedSvar === 'tom' ? existing?.lon : stedSvar.lon,
        mgrs: stedSvar === 'tom' ? existing?.mgrs : stedSvar.mgrs,
        gjelderFraMs, gjelderTilMs,
        imageUrl: imgUrl.field.value.trim() || undefined,
        boxes: annotator ? annotator.getBoxes() : existing?.boxes ?? [],
      };
      this.products.set(p.id, p);
      this.opts.onShare(p);
      this.renderList();
      this.openView(p);
    };
    const cancel = iconBtn('Avbryt', () => this.closeEditor()); cancel.className = 'intel-cancel';
    const actions = div('intel-actions'); actions.append(save, cancel);
    card.append(actions);
    this.editorEl.append(card);
  }

  private closeEditor() {
    this.editorEl.classList.remove('open');
    this.editorEl.innerHTML = '';
  }
}

/** Deterministisk utkast fra koblede meldinger (KI kan erstatte dette senere). */
export function draftFromReports(reports: CombatReport[]): string {
  const lines = reports.map((r) => `• [${r.type}] ${reportSummary(r)} (${new Date(r.tsMs).toISOString().slice(0, 16).replace('T', ' ')}Z)`);
  return `VURDERING (utkast fra ${reports.length} melding(er)):\n\nGrunnlag:\n${lines.join('\n')}\n\nVurdering:\n`;
}

// ── Rom/tid-visning (rene strengbyggere — testbare uten DOM) ─────────────────

/** Stedet slik kortet viser det. Eldre produkter kan ha grader uten MGRS. */
export function stedTekst(p: Pick<IntelProduct, 'mgrs' | 'lat' | 'lon'>): string {
  if (p.mgrs) return formatMgrs(p.mgrs);
  if (p.lat !== undefined && p.lon !== undefined) {
    const mgrs = latLonToMgrs({ lat: p.lat, lon: p.lon });
    return mgrs ? formatMgrs(mgrs) : `${p.lat.toFixed(5)}, ${p.lon.toFixed(5)}`;
  }
  return 'ikke angitt (eldre produkt)';
}

/** Gyldighetsvinduet i klartekst. Åpne ender sies eksplisitt. */
export function tidsromTekst(p: Pick<IntelProduct, 'gjelderFraMs' | 'gjelderTilMs'>): string {
  const f = (ms: number) => new Date(ms).toISOString().slice(0, 16).replace('T', ' ') + 'Z';
  if (p.gjelderFraMs !== undefined && p.gjelderTilMs !== undefined) return `${f(p.gjelderFraMs)} – ${f(p.gjelderTilMs)}`;
  if (p.gjelderFraMs !== undefined) return `fra ${f(p.gjelderFraMs)}`;
  if (p.gjelderTilMs !== undefined) return `til ${f(p.gjelderTilMs)}`;
  return 'ikke angitt';
}

// ── DOM-hjelpere ────────────────────────────────────────────────────────────
function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function txt(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
function iconBtn(text: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button'); b.type = 'button'; b.className = 'intel-icon-btn'; b.textContent = text; b.onclick = onClick; return b;
}
function kv(k: string, v: string): HTMLElement {
  const row = div('intel-kv');
  row.append(txt('intel-k', k), txt('intel-v', v));
  return row;
}
function input(label: string, value: string) {
  const wrap = div('intel-field');
  wrap.append(txt('intel-field-label', label));
  const field = document.createElement('input'); field.className = 'intel-input'; field.value = value;
  wrap.append(field);
  return { wrap, field };
}
/** datetime-local-felt for gyldighetsvinduet (lokal tid; ms regnes ved lagring). */
function datofelt(label: string, ms?: number) {
  const wrap = div('intel-field');
  wrap.append(txt('intel-field-label', label));
  const field = document.createElement('input');
  field.className = 'intel-input';
  field.type = 'datetime-local';
  field.value = msTilDatetimeLocal(ms);
  wrap.append(field);
  return { wrap, field };
}
function textarea(label: string, value: string) {
  const wrap = div('intel-field');
  wrap.append(txt('intel-field-label', label));
  const field = document.createElement('textarea'); field.className = 'intel-input intel-textarea'; field.value = value; field.rows = 5;
  wrap.append(field);
  return { wrap, field };
}
function select(label: string, options: readonly string[], value: string, optLabel?: (v: string) => string) {
  const wrap = div('intel-field');
  wrap.append(txt('intel-field-label', label));
  const field = document.createElement('select'); field.className = 'intel-input';
  for (const o of options) {
    const opt = document.createElement('option'); opt.value = o; opt.textContent = optLabel ? optLabel(o) : o;
    field.append(opt);
  }
  field.value = value;
  wrap.append(field);
  return { wrap, field };
}
function shortId(id: string): string { return id.replace(/^@/, '').replace(/:.*$/, ''); }
